import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import GlobalStyle from './styles/GlobalStyle';
import { forgeTheme } from './styles/theme';

// Firebase Auth Provider
import { AuthProvider, useAuth } from './firebase/AuthContext';
import { auth } from './firebase/config';

// Analytics tracking
import { initVisitorSession, trackPageView } from './firebase/analyticsService';
import usePageTracking from './hooks/usePageTracking';

// Cart Provider
import { CartProvider } from './context/CartContext';

// Components
import Header from './components/Header';
import Footer from './components/Footer';
import PrivateRoute from './components/auth/PrivateRoute';
import AdminRoute from './components/auth/AdminRoute';
import FirebaseSetup from './components/auth/FirebaseSetup';
import Cart from './components/cart/Cart';
import Checkout from './components/checkout/Checkout';
import OrderConfirmation from './components/checkout/OrderConfirmation';
import OrderDetails from './components/orders/OrderDetails';
import ScrollToTop from './components/ScrollToTop';

// Admin Components
import AdminDashboard from './components/admin/AdminDashboard';
import ProductManagement from './components/admin/ProductManagement';
import ProductForm from './components/admin/ProductForm';
import OrderManagement from './components/admin/OrderManagement';
import CustomerManagement from './components/admin/CustomerManagement';
import UserAccountsManagement from './components/admin/UserAccountsManagement';
import AnalyticsDashboard from './components/admin/AnalyticsDashboard';
import PageAnalytics from './components/admin/PageAnalytics';
import SettingsPage from './components/admin/SettingsPage';
import InitializeSettingsPage from './components/admin/InitializeSettingsPage';
import SetupAdmin from './components/admin/SetupAdmin';
import TreasureHuntAdmin from './components/admin/TreasureHuntAdmin';
import HeroManagement from './components/admin/HeroManagement';

// Pages
import HomePage from './pages/HomePage';
import CategoriesPage from './pages/CategoriesPage';
import ProductPage from './pages/ProductPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import LegalPoliciesPage from './pages/LegalPoliciesPage';
import UpdateSettings from './pages/UpdateSettings';
import TestShippingLabel from './pages/TestShippingLabel';
import BlogPage from './pages/BlogPage';
import TreasureHuntPage from './pages/TreasureHuntPage';
import LeaderboardPage from './pages/LeaderboardPage';

// Auth Pages
import Login from './components/auth/Login';
import Signup from './components/auth/Signup';
import ForgotPassword from './components/auth/ForgotPassword';
import Dashboard from './components/dashboard/Dashboard';
import UserProfile from './components/profile/UserProfile';
import StripeIntegration from './components/payments/StripeIntegration';

// Component to track page views
function PageViewTracker() {
  const location = useLocation();
  usePageTracking(); // Track page visits in Firestore
  
  useEffect(() => {
    trackPageView(location.pathname);
  }, [location]);
  
  return null;
}

// Component to initialize visitor session
function VisitorTracker() {
  const { currentUser } = useAuth();
  
  useEffect(() => {
    // Initialize visitor session when app loads
    const initSession = async () => {
      try {
        await initVisitorSession(currentUser?.uid || null);
      } catch (error) {
        console.error('Error initializing visitor session:', error);
      }
    };
    
    initSession();
  }, [currentUser]);
  
  return null;
}

function App() {
  const [firebaseConfigured, setFirebaseConfigured] = useState(false);
  const [checkingConfig, setCheckingConfig] = useState(true);

  useEffect(() => {
    // Check if Firebase is properly configured
    const checkFirebaseConfig = async () => {
      try {
        // If the apiKey is still the placeholder, Firebase is not configured
        const isConfigured = auth.app.options.apiKey !== "YOUR_API_KEY";
        setFirebaseConfigured(isConfigured);
      } catch (error) {
        console.error("Firebase configuration error:", error);
        setFirebaseConfigured(false);
      } finally {
        setCheckingConfig(false);
      }
    };

    checkFirebaseConfig();
  }, []);

  if (checkingConfig) {
    return <div>Loading...</div>;
  }

  return (
    <ThemeProvider theme={forgeTheme}>
      <GlobalStyle />
      {!firebaseConfigured ? (
        <FirebaseSetup />
      ) : (
        <AuthProvider>
          <CartProvider>
            <Router>
              <ScrollToTop />
              <VisitorTracker />
              <PageViewTracker />
              <Header />
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/categories" element={<CategoriesPage />} />
                <Route path="/product/:id" element={<ProductPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/blog" element={<BlogPage />} />
                <Route path="/treasure-hunt" element={<TreasureHuntPage />} />
                <Route path="/leaderboard" element={<LeaderboardPage />} />
                <Route path="/legal-policies" element={<LegalPoliciesPage />} />
                <Route path="/cart" element={<Cart />} />
                
                {/* Auth Routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                
                {/* Protected Routes */}
                <Route element={<PrivateRoute />}>
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/profile" element={<UserProfile />} />
                  <Route path="/payment-methods" element={<StripeIntegration />} />
                  <Route path="/checkout" element={<Checkout />} />
                  <Route path="/order-confirmation" element={<OrderConfirmation />} />
                  <Route path="/orders/:id" element={<OrderDetails />} />
                  <Route path="/admin-setup" element={<SetupAdmin />} />
                </Route>
                
                {/* Admin Routes - Protected by AdminRoute */}
                <Route element={<AdminRoute />}>
                  <Route path="/admin" element={<AdminDashboard />} />
                  <Route path="/admin/products" element={<ProductManagement />} />
                  <Route path="/admin/products/add" element={<ProductForm />} />
                  <Route path="/admin/products/edit/:id" element={<ProductForm />} />
                  <Route path="/admin/hero" element={<HeroManagement />} />
                  <Route path="/admin/orders" element={<OrderManagement />} />
                  <Route path="/admin/customers" element={<CustomerManagement />} />
                  <Route path="/admin/user-accounts" element={<UserAccountsManagement />} />
                  <Route path="/admin/analytics" element={<AnalyticsDashboard />} />
                  <Route path="/admin/page-analytics" element={<PageAnalytics />} />
                  <Route path="/admin/settings" element={<SettingsPage />} />
                  <Route path="/admin/initialize-settings" element={<InitializeSettingsPage />} />
                  <Route path="/admin/update-settings" element={<UpdateSettings />} />
                  <Route path="/admin/test-shipping-label" element={<TestShippingLabel />} />
                  <Route path="/admin/treasure-hunt" element={<TreasureHuntAdmin />} />
                </Route>
              </Routes>
              <Footer />
            </Router>
          </CartProvider>
        </AuthProvider>
      )}
    </ThemeProvider>
  );
}

export default App;
